import { CIAppliedFilter, CIAppliedListFilter, SearchInput } from 'ccs-ui-components';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './AssociationSourceWrapper.module.scss';

import SourceAssociationList from './SourceAssociationList';

import { AssociationListFilter } from '../../../../_components/association-filter/AssociationListFilter';
import { FILTER_TYPE, SORT_STRINGS, allStatusList } from '../../../../constants';
import useSourceEventAssociation from '../../../../hooks/events/association-list/useSourceEventAssociation';
import { IActiveSource } from '../../../../store/actions/types/sources/GetAllSourceRegistries';
import { initialState } from '../../../../store/reducer/sources/SourceRegistryListReducer';

interface IAssociationSourcesWrapper {
  eventName: string,
  eventVersion: string
}
const AssociationSourcesWrapper = ({ eventName, eventVersion }: IAssociationSourcesWrapper) => {
  /* Hooks */
  const { sourceEventAssociation, isLoading, errorMessage } = useSourceEventAssociation();
  const { t: translate } = useTranslation();
  /* Local states */
  const [searchTemp, setSearchTemp] = useState('');
  const [queryParams, setQueryParams] = useState<{ [key: string]: string }>({
    page: String(0),
    item: String(10),
    sourceName: '',
    status: '',
    sort: 'sourceName',
    type: SORT_STRINGS.ASC
  });
  const [data, setData] = useState<IActiveSource>(initialState.sourceData);
  /* Local Variables */
  const debounceDelay = 300;

  const debounceHandler = useCallback(debounce((value: string) => {
    setQueryParams((pre) => ({
      ...pre,
      status: queryParams.status,
      page: String(0),
      item: queryParams.item,
      sourceName: value
    }));
  }, debounceDelay), [searchTemp]);

  /**
 * @method searchHandler
 * @description Handles the search event and updates the search term state.
 * @param event - The change event triggered by the input element.
 */
  const searchHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTemp((event.target.value).trim());
    debounceHandler((event.target.value).trim());
  };

  useEffect(() => {
    sourceEventAssociation(queryParams, eventName, eventVersion)?.then(async (sourceRecords) => {
      if (sourceRecords.sourceRecords) {
        setData(sourceRecords.sourceRecords);
      }
    });
  }, [queryParams]);

  const allStatusMap: { [key: string]: string } = {};
  allStatusList.forEach((s) => { allStatusMap[s.value] = s.label; });

  const appliedFilterData: CIAppliedFilter[] = useMemo(() => {
    const data: CIAppliedFilter[] = [];
    const filterStatusList: string[] = queryParams?.status?.split(',');
    if (!queryParams?.status) {
      return [];
    }
    if (filterStatusList?.length > 0 && filterStatusList?.length < allStatusList.length) {
      filterStatusList?.forEach((f: any) => data.push({ text: allStatusMap[f], type: FILTER_TYPE.STATUS, value: f }));
    }
    return data;
  }, [queryParams]);

  const appliedFilterChangeHandler = (filters: CIAppliedFilter[]) => {
    const statusList: string[] = [];
    filters.forEach((f) => {
      if (f.type === FILTER_TYPE.STATUS) {
        statusList?.push(f.value);
      }
    });
    setQueryParams((pre) => ({
      ...pre,
      status: statusList?.join(',') ?? '',
    }));
  };

  return (
    <>
      <div className={classes.filters}>
        <div className={classes['search-filter-div']}>
          <AssociationListFilter
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            isSourceSelected
          />
          <div className={classes.search}>
            <SearchInput
              label="search"
              type="search"
              placeholder={String(translate('event_associations.lbl_source_search_text'))}
              onChange={searchHandler}
              data-testid="filterSearchInput"
              value={searchTemp}
            />
          </div>
        </div>
        <CIAppliedListFilter className={classes['applied-filters']} totalRecords={data?.totalRecords} appliedFilters={appliedFilterData} onFilterChange={appliedFilterChangeHandler} />
      </div>
      <SourceAssociationList sourceData={data} setQueryParams={setQueryParams} queryParams={queryParams} errorMessage={errorMessage} isLoading={isLoading} />
    </>
  );
};

export default AssociationSourcesWrapper;
